import _get from 'lodash.get';

import { CreditCardBrand, Nullable } from '@types-internal';
import { defaultString } from '@utils/utils';

import {
  AdyenState,
  CardErrors,
  getFocusedElement,
  getLastFourDigits,
  getFromAdyenCardBrand,
  isHolderNameValid,
  maybeErrorForHolderName,
  ParsedAdyenPaymentMethod,
  AdyenStateErrorMessages,
  AllErrors,
  setupRequiredMessages,
} from './useAdyenState.static';

export type BancontactCardErrors = Omit<CardErrors, 'encryptedSecurityCode'>;

export interface BancontactAllErrors extends BancontactCardErrors {
  holderName: Nullable<string>;
}

export type BancontactAdyenStateErrorMessages = Omit<AdyenStateErrorMessages, 'requiredCVVMessage'>;

export type ParsedBancontactAdyenPaymentMethod = Omit<
  ParsedAdyenPaymentMethod,
  'encryptedSecurityCode'
>;

export interface ParsedBancontactAdyenState {
  isValid: boolean;
  paymentMethod: ParsedBancontactAdyenPaymentMethod;
  errors: BancontactCardErrors;
}

export type BancontactAdyenState = Omit<AdyenState, 'paymentMethod'> & {
  paymentMethod: Omit<AdyenState['paymentMethod'], 'encryptedSecurityCode'>;
};

export interface BancontactAdyenCreditCardFormData {
  type: string;
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  holderName: string;
  storeInVault: boolean;
  creditCardBrand: Nullable<CreditCardBrand>;
  lastFour: string;
}

/*
 * Errors
 */

export const DEFAULT_BANCONTACT_CARD_ERRORS: BancontactCardErrors = {
  encryptedCardNumber: null,
  encryptedExpiryMonth: null,
  encryptedExpiryYear: null,
  encryptedExpiryDate: null,
};

export const DEFAULT_BANCONTACT_ERRORS: BancontactAllErrors = {
  ...DEFAULT_BANCONTACT_CARD_ERRORS,
  holderName: null,
};

/*
 * Parsing Adyen's state in the 'onChange' callback
 */

const DEFAULT_ON_CHANGE_PAYMENT_METHOD = {
  type: '',
  encryptedCardNumber: '',
  encryptedExpiryMonth: '',
  encryptedExpiryYear: '',
};

export const DEFAULT_BANCONTACT_ADYEN_STATE: BancontactAdyenState = {
  isValid: false,
  storeInVault: false,
  creditCardBrand: null,
  lastFour: '',
  paymentMethod: {
    ...DEFAULT_ON_CHANGE_PAYMENT_METHOD,
    holderName: '',
  },
};

export function getPaymentMethodField(stateOnChange: unknown, name: string) {
  return defaultString(_get(stateOnChange, `data.paymentMethod.${name}`));
}

export function getErrorField(stateOnChange: unknown, name: string) {
  const value = _get(stateOnChange, `errors.${name}.errorI18n`);
  return typeof value === 'string' ? value : null;
}

// This extracts the data we need from the stateOnChange from Adyen, and uses to the appropriate default values for each field.
export function parseStateOnChange(stateOnChange: unknown): ParsedBancontactAdyenState {
  return {
    isValid: _get(stateOnChange, 'isValid', false),
    paymentMethod: {
      type: getPaymentMethodField(stateOnChange, 'type'),
      encryptedCardNumber: getPaymentMethodField(stateOnChange, 'encryptedCardNumber'),
      encryptedExpiryMonth: getPaymentMethodField(stateOnChange, 'encryptedExpiryMonth'),
      encryptedExpiryYear: getPaymentMethodField(stateOnChange, 'encryptedExpiryYear'),
    },
    errors: {
      encryptedCardNumber: getErrorField(stateOnChange, 'encryptedCardNumber'),
      encryptedExpiryMonth: getErrorField(stateOnChange, 'encryptedExpiryMonth'),
      encryptedExpiryYear: getErrorField(stateOnChange, 'encryptedExpiryYear'),
      // The month field and the date field are on the same height in the UI. Only one error message can be displayed.
      // => we pick the first non-null error, priority (arbitrarily) given to the month field.
      encryptedExpiryDate:
        getErrorField(stateOnChange, 'encryptedExpiryMonth') ??
        getErrorField(stateOnChange, 'encryptedExpiryYear'),
    },
  };
}

export function isBancontactAdyenStateValid(state: BancontactAdyenState): boolean {
  return (
    state.isValid &&
    state.paymentMethod.type !== '' &&
    state.paymentMethod.encryptedCardNumber !== '' &&
    state.paymentMethod.encryptedExpiryMonth !== '' &&
    state.paymentMethod.encryptedExpiryYear !== '' &&
    state.paymentMethod.holderName !== ''
  );
}

export function convertBancontactAdyenState(
  state: BancontactAdyenState,
): BancontactAdyenCreditCardFormData {
  return {
    ...state.paymentMethod,
    storeInVault: state.storeInVault,
    creditCardBrand: state.creditCardBrand,
    lastFour: state.lastFour,
  };
}

// We have a field error from Adyen only if the customer actually touched the related input.
// So 'no error' means that either the field is valid or empty: if empty, we had the requiredMessage.
export function addRequiredMessageIfNeeded(
  errors: BancontactAllErrors,
  state: BancontactAdyenState,
  requiredMessages: BancontactAdyenStateErrorMessages | string,
): Partial<BancontactAllErrors> {
  const newErrors: Partial<BancontactAllErrors> = {};

  if (typeof requiredMessages === 'string') {
    if (
      errors.encryptedExpiryDate === null &&
      (state.paymentMethod.encryptedExpiryMonth === '' ||
        state.paymentMethod.encryptedExpiryYear === '')
    ) {
      newErrors.encryptedExpiryDate = requiredMessages;
    }
    requiredMessages = {
      requiredNameMessage: requiredMessages,
      requiredMonthMessage: requiredMessages,
      requiredNumberMessage: requiredMessages,
      requiredYearMessage: requiredMessages,
    };
  }

  setupRequiredMessages(
    errors as AllErrors,
    state as AdyenState,
    requiredMessages as AdyenStateErrorMessages,
    newErrors as Partial<BancontactAllErrors>,
  );

  return newErrors;
}

export function areBancontactIdenticalCardErrors(
  currentErrors: BancontactCardErrors,
  newErrors: BancontactCardErrors,
) {
  return (
    currentErrors.encryptedCardNumber === newErrors.encryptedCardNumber &&
    currentErrors.encryptedExpiryMonth === newErrors.encryptedExpiryMonth &&
    currentErrors.encryptedExpiryYear === newErrors.encryptedExpiryYear &&
    currentErrors.encryptedExpiryDate === newErrors.encryptedExpiryDate
  );
}

export {
  getFocusedElement,
  getLastFourDigits,
  getFromAdyenCardBrand,
  isHolderNameValid,
  maybeErrorForHolderName,
};
